import { RESOURCE_SLUG_PATTERN } from "../slug-grammar.js";
import { APP_POOL_REGION_PATTERN } from "../region-grammar.js";
import "../app-pool.js";
import { onboardingFailureCodeSchema, onboardingMessageSchema, onboardingProgressIssue, onboardingStateSchema, onboardingStepStateSchema } from "./onboarding-state.js";
import { SIGN_IN_TYPES_OFFERED, SIGN_IN_TYPE_SPECS, clientSecretOf, signInInputSchema, signInViewSchema, withoutClientSecret } from "./sign-in-types.js";
import { z } from "zod";
//#region src/onboarding/onboarding-request.ts
/**
* The tenant onboarding request: what an operator asks for, what the CP stores, and what a read
* returns.
*
* Plan `2026-10-16-customer-onboarding`, workstream WS-1, decisions D2, D12, D13, D14, D15 and D30,
* defaults F1, F2, F3 and F8, rulings R-055, R-063 item 5, R-066 item 1 and R-070.
*
* Who uses it:
*
* - provisioning-svc (WS-6) binds {@link onboardingRequestSchema} on
*   `POST /v1/onboarding-requests`, stores {@link withoutSignInSecret} of it in
*   `provisioning.onboarding_requests`, and answers reads with {@link onboardingRequestViewSchema}.
*   It binds {@link onboardingRequestIdParamSchema} on `GET /v1/onboarding-requests/:id`,
*   `POST /v1/onboarding-requests/:id/retry` and `PUT /v1/onboarding-requests/:id/client-secret`,
*   whose body is `onboardingClientSecretBodySchema`.
* - site-admin-api (WS-17) binds the same schemas on its proxy routes and on the MCP tool
*   `onboard_tenant` (overview section 8: the same zod schema).
* - The console wizard (WS-16) and `zion-ops tenant onboard` (WS-18) build the body.
*
* The request holds no plan tier (D13) and offers single tenancy only (D15). The client secret is
* write-only, and no stored or returned form has it (D12). The intake body carries it once, or
* carries the marker `clientSecretLater: true`; with the marker the secret arrives later through
* `PUT /v1/onboarding-requests/:id/client-secret` (R-055).
*
* Every schema here is strict and has refinements and no transform, so `createOpenApiDocument()`
* can represent it. A refinement does not reach the JSON Schema, so each refusal message names its
* rule.
*/
/** The tenancies the intake offers. Single tenant only (D15). */
const ONBOARDING_TENANCIES = ["single_tenant"];
/** The clouds the intake offers. A local data plane is not a customer cloud (default F3). */
const ONBOARDING_CLOUDS = ["gcp", "aws"];
/** The longest tenant name, in characters. */
const ONBOARDING_TENANT_NAME_MAX_LENGTH = 128;
/**
* A tenant display name: no leading or trailing white space and no control character. One
* character or more.
*/
const ONBOARDING_TENANT_NAME_PATTERN = /^[^\s\p{Cc}](?:[^\p{Cc}]*[^\s\p{Cc}])?$/u;
/**
* The longest tenant slug, in characters: one DNS label, because the boutique host is
* `<tenant>.<dp>.<base>` (D24).
*/
const ONBOARDING_TENANT_SLUG_MAX_LENGTH = 63;
/**
* The org slugs that the platform keeps for itself: the one value `kernel`, the label of the kernel
* origin `kernel.<dp host>`.
*
* The same set as `RESERVED_ORG_SLUGS` of the platform-api create-org schema (plan
* 2026-10-03-kernel-apps, R-029). A contract package cannot import a service, so platform-api keeps
* its own copy until it imports this one. The intake refuses a reserved slug before platform-api
* does, so the job cannot fail on it in `creating_tenant`.
*/
const RESERVED_ORG_SLUGS = /* @__PURE__ */ new Set(["kernel"]);
/** The message of a reserved tenant slug. */
const RESERVED_ORG_SLUG_MESSAGE = "tenantSlug is reserved for the platform";
/**
* The longest data plane name, in characters. The same value as `DP_NAME_MAX_LENGTH` of the
* provisioning-svc create schema, whose source is the 20 of the `dpName` field of the XRD (the GKE
* node pool name budget).
*/
const ONBOARDING_DATA_PLANE_NAME_MAX_LENGTH = 20;
/**
* A data plane name: one lower-case DNS label. The same language as `DP_NAME_PATTERN` of the
* provisioning-svc create schema. provisioning-svc still applies its reserved names and its AWS
* rules at intake, and answers `DATA_PLANE_NAME_RESERVED`.
*/
const ONBOARDING_DATA_PLANE_NAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
/** The longest owner email address, in characters (RFC 5321 path limit). */
const ONBOARDING_OWNER_EMAIL_MAX_LENGTH = 254;
/** The most allowed domains one request names. */
const ONBOARDING_ALLOWED_DOMAINS_MAX = 32;
/** The longest allowed domain, in characters. */
const ONBOARDING_DOMAIN_MAX_LENGTH = 253;
/**
* An email domain: two or more lower-case DNS labels of 1 to 63 characters, and the last label
* starts with a letter, so an IP address is refused.
*/
const ONBOARDING_DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
/** The most apps one request licenses. */
const ONBOARDING_APPS_MAX = 64;
/** The longest app slug, in characters. */
const ONBOARDING_APP_SLUG_MAX_LENGTH = 63;
/** The tenant slug of a request: {@link RESOURCE_SLUG_PATTERN}, at most 63 characters, not reserved. */
const onboardingTenantSlugSchema = z.string().max(63).regex(RESOURCE_SLUG_PATTERN, "tenantSlug must be lowercase alphanumeric with hyphens, no leading or trailing hyphen").refine((slug) => !RESERVED_ORG_SLUGS.has(slug), { message: RESERVED_ORG_SLUG_MESSAGE });
/** One allowed email domain: {@link ONBOARDING_DOMAIN_PATTERN}, at most 253 characters. */
const onboardingDomainSchema = z.string().max(253, { abort: true }).regex(ONBOARDING_DOMAIN_PATTERN, "a domain is two or more lowercase DNS labels, such as acme.com");
/** The allowed domains: 0 to 32 domains, each once. An empty list sets no domain gate. */
const onboardingAllowedDomainsSchema = z.array(onboardingDomainSchema).max(32).refine((domains) => new Set(domains).size === domains.length, { message: "each allowed domain appears once" });
/** One app slug: {@link RESOURCE_SLUG_PATTERN}, at most 63 characters. */
const onboardingAppSlugSchema = z.string().max(63).regex(RESOURCE_SLUG_PATTERN, "an app slug is lowercase alphanumeric with hyphens");
/** The apps to license: 0 to 64 app slugs, each once. */
const onboardingAppSlugsSchema = z.array(onboardingAppSlugSchema).max(64).refine((apps) => new Set(apps).size === apps.length, { message: "each app appears once" });
/** The owner email address: at most 254 characters, then `z.email()`. */
const onboardingOwnerEmailSchema = z.string().max(254, { abort: true }).check(z.email());
/** The message of an owner email whose domain is outside a non-empty list of allowed domains. */
const ONBOARDING_OWNER_DOMAIN_MESSAGE = "ownerEmail must be at one of the allowedDomains, or the domain gate refuses the owner";
/** Trim, then fold `A` to `Z` only: the `normalizeEmail` rule of the core-api domain gate. */
function foldAsciiCase(value) {
	return value.trim().replace(/[A-Z]/g, (c) => c.toLowerCase());
}
/**
* Tell whether the core-api domain gate lets the owner in.
*
* Callers: the refinements of {@link onboardingRequestSchema}, {@link onboardingRequestRecordSchema}
* and the org seed schema, and a form that checks the pair before it sends it.
*
* Input: an owner email and the allowed domains. Output: `true` for an empty list, because an
* empty list sets no domain gate. Else `true` only when the text after the last `@` of the email
* equals one entry. Both sides are trimmed and fold `A` to `Z` only, as `normalizeEmail` does in
* `IdentityService.handleCallback`. It never throws and changes no input.
*/
function onboardingOwnerDomainAllowed(ownerEmail, allowedDomains) {
	if (allowedDomains.length === 0) return true;
	const domain = foldAsciiCase(ownerEmail.slice(ownerEmail.lastIndexOf("@") + 1));
	return allowedDomains.some((entry) => foldAsciiCase(entry) === domain);
}
/**
* The refinement params of the owner domain rule. It runs only when every field parsed, so a bad
* email or a bad domain list gets its own issue and no second one.
*/
const OWNER_DOMAIN_RULE = {
	path: ["ownerEmail"],
	message: ONBOARDING_OWNER_DOMAIN_MESSAGE,
	when: (payload) => payload.issues.length === 0
};
/**
* The message of a request whose sign-in type is outside `SIGN_IN_TYPES_OFFERED`: today `workos`,
* which every door refuses until an email-trust ruling exists (R-063 item 3, R-066 item 1).
*/
const ONBOARDING_SIGN_IN_NOT_OFFERED_MESSAGE = "signIn.type is not offered: choose password, passkey, google, github or oidc";
/**
* The message of an `oidc` request with no allowed domain. An enterprise sign-in trusts the email of
* the IdP only inside the allowed domains, so a seed with no domain refuses every enterprise sign-in
* (R-063 item 5). The text also names `workos`, but no `workos` request gets it: the offered-type
* rule refuses `workos` first, and the `when` gate of the enterprise domain rule then skips it.
*/
const ONBOARDING_ENTERPRISE_DOMAIN_MESSAGE = "an oidc or workos sign-in needs one allowedDomains entry or more";
/** True when the intake offers the sign-in type (R-070 item 2). */
function signInTypeOffered(type) {
	return SIGN_IN_TYPES_OFFERED.includes(type);
}
/**
* The refinement params of the offered-type rule. It runs beside a continuable issue, such as a
* pattern refusal inside `signIn`, so such a `workos` request names the type too (default F3). zod
* skips it after an issue that aborts the parse, such as an unknown key or a field of the wrong
* type, also when
* `signIn` parsed; a `workos` request with such an issue gets no `signIn.type` issue.
*/
const OFFERED_TYPE_RULE = {
	path: ["signIn", "type"],
	message: ONBOARDING_SIGN_IN_NOT_OFFERED_MESSAGE
};
/**
* The refinement params of the enterprise domain rule. Its `when` gate runs it only when no earlier
* issue exists, so a refused type or a bad domain list gets its own issue and no second one. The
* owner domain rule runs after it.
*/
const ENTERPRISE_DOMAIN_RULE = {
	path: ["allowedDomains"],
	message: ONBOARDING_ENTERPRISE_DOMAIN_MESSAGE,
	when: (payload) => payload.issues.length === 0
};
/** The fields every form of a request shares. The sign-in field differs per form. */
const requestFields = {
	tenancy: z.enum(ONBOARDING_TENANCIES),
	cloud: z.enum(ONBOARDING_CLOUDS),
	region: z.string().regex(APP_POOL_REGION_PATTERN, "region must be a cloud region name such as us-central1"),
	tenantName: z.string().min(1).max(128).regex(ONBOARDING_TENANT_NAME_PATTERN, "tenantName has no leading or trailing space and no control character"),
	tenantSlug: onboardingTenantSlugSchema,
	dataPlaneName: z.string().min(1).max(20).regex(ONBOARDING_DATA_PLANE_NAME_PATTERN, "dataPlaneName must be a single lowercase DNS label"),
	ownerEmail: onboardingOwnerEmailSchema,
	allowedDomains: onboardingAllowedDomainsSchema,
	apps: onboardingAppSlugsSchema
};
/**
* The body of `POST /v1/onboarding-requests`.
*
* Fields, every one required:
* - `tenancy`: `single_tenant`.
* - `cloud`: `gcp` or `aws`.
* - `region`: a region name in the grammar of `APP_POOL_REGION_PATTERN`. provisioning-svc checks it
*   against the allowed regions of the cloud and answers `REGION_NOT_ALLOWED`.
* - `tenantName`: 1 to 128 characters, no leading or trailing space, no control character.
* - `tenantSlug`: {@link onboardingTenantSlugSchema}.
* - `dataPlaneName`: 1 to 20 characters of {@link ONBOARDING_DATA_PLANE_NAME_PATTERN}. The job
*   names the dedicated data plane with it, and the GCP project `zdp-<dataPlaneName>-<4 hex>`.
* - `ownerEmail`: the address the owner invitation goes to (D11).
* - `allowedDomains`: {@link onboardingAllowedDomainsSchema}.
* - `signIn`: `signInInputSchema`, with the write-only `clientSecret` or the marker
*   `clientSecretLater: true` (R-055 item 1).
* - `apps`: {@link onboardingAppSlugsSchema}. provisioning-svc answers `APP_NOT_FOUND` for an
*   unknown slug.
*
* The rules across fields, in the order they run:
* 1. The sign-in type is one of `SIGN_IN_TYPES_OFFERED`. A `workos` request is an issue at
*    `signIn.type` with {@link ONBOARDING_SIGN_IN_NOT_OFFERED_MESSAGE} (R-070 item 2).
* 2. An `oidc` request names one allowed domain or more. Else an issue at `allowedDomains` with
*    {@link ONBOARDING_ENTERPRISE_DOMAIN_MESSAGE} (R-063 item 5). The rule tests the enterprise
*    category, which holds `workos` too, but rule 1 refuses a `workos` request first.
* 3. The owner email is at one of the allowed domains, or the list is empty
*    ({@link onboardingOwnerDomainAllowed}). Else an issue at `ownerEmail`.
* Rules 2 and 3 run only when no earlier issue exists, so a request gets the first broken rule.
*
* These rules belong to the intake alone. {@link onboardingRequestRecordSchema} keeps rule 3 only,
* so a stored row or a view of an older request still parses (R-070 item 2).
*
* Errors: an unknown key at any depth, a missing field or a broken rule is a 400 from the pipe. The
* schema has no transform: the parse returns the body it read.
*/
const onboardingRequestSchema = z.strictObject({
	...requestFields,
	signIn: signInInputSchema
}).refine((request) => signInTypeOffered(request.signIn.type), OFFERED_TYPE_RULE).refine((request) => SIGN_IN_TYPE_SPECS[request.signIn.type].category !== "enterprise" || request.allowedDomains.length > 0, ENTERPRISE_DOMAIN_RULE).refine((request) => onboardingOwnerDomainAllowed(request.ownerEmail, request.allowedDomains), OWNER_DOMAIN_RULE);
/**
* The stored form of a request: {@link onboardingRequestSchema} with `signIn` in its view form, so
* no `clientSecret`. Strict, so a row that still carries the secret fails the parse (default F8).
* The view form keeps the marker `clientSecretLater: true` (R-055 item 1). Of the rules across
* fields only the owner domain rule applies: the offered-type rule and the enterprise domain rule
* guard the intake, and a reader never refuses a row for them (R-070 item 2).
*/
const onboardingRequestRecordSchema = z.strictObject({
	...requestFields,
	signIn: signInViewSchema
}).refine((record) => onboardingOwnerDomainAllowed(record.ownerEmail, record.allowedDomains), OWNER_DOMAIN_RULE);
/**
* Turn a request into its stored form.
*
* Caller: provisioning-svc, before the insert of the request row. Output: a new record with every
* field of the request and `signIn` from `withoutClientSecret`. The input is not changed.
*/
function withoutSignInSecret(request) {
	return {
		tenancy: request.tenancy,
		cloud: request.cloud,
		region: request.region,
		tenantName: request.tenantName,
		tenantSlug: request.tenantSlug,
		dataPlaneName: request.dataPlaneName,
		ownerEmail: request.ownerEmail,
		allowedDomains: [...request.allowedDomains],
		signIn: withoutClientSecret(request.signIn),
		apps: [...request.apps]
	};
}
/**
* Read the client secret of a request: the `clientSecret` of a `google`, `github` or `oidc`
* sign-in, else `null`. A sign-in with the marker `clientSecretLater: true` answers `null`, so
* provisioning-svc holds no value and the job waits in `awaiting_client_secret` (R-055 item 2).
* provisioning-svc writes a value once to the data plane secret path (D12).
*/
function signInSecretOf(request) {
	return clientSecretOf(request.signIn);
}
/**
* The route parameter of `GET /v1/onboarding-requests/:id` and of its `retry` and `client-secret`
* routes: a UUID.
*/
const onboardingRequestIdParamSchema = z.strictObject({ id: z.uuid() });
/**
* The answer of `GET /v1/onboarding-requests/:id`, and of the create and retry routes.
*
* Fields:
* - `id`: the request id.
* - `request`: the stored form, {@link onboardingRequestRecordSchema}.
* - `state`, `failedInState`, `failureCode`, `failureMessage`, `tenantId`, `dataPlaneId`: the
*   progress of the job. `onboardingProgressIssue` must answer `null` for them.
* - `requestedBy`: the id of the operator who sent the request.
* - `createdAt`, `updatedAt`: ISO 8601 times with an offset.
*
* Strict at every depth. The refinement applies the rules of `onboardingProgressIssue`.
*/
const onboardingRequestViewSchema = z.strictObject({
	id: z.uuid(),
	request: onboardingRequestRecordSchema,
	state: onboardingStateSchema,
	failedInState: onboardingStepStateSchema.nullable(),
	failureCode: onboardingFailureCodeSchema.nullable(),
	failureMessage: onboardingMessageSchema.nullable(),
	tenantId: z.uuid().nullable(),
	dataPlaneId: z.uuid().nullable(),
	requestedBy: z.uuid(),
	createdAt: z.iso.datetime({ offset: true }),
	updatedAt: z.iso.datetime({ offset: true })
}).superRefine((view, ctx) => {
	const issue = onboardingProgressIssue(view);
	if (issue !== null) ctx.addIssue({
		code: "custom",
		path: ["state"],
		message: issue
	});
});
//#endregion
export { ONBOARDING_ALLOWED_DOMAINS_MAX, ONBOARDING_APPS_MAX, ONBOARDING_APP_SLUG_MAX_LENGTH, ONBOARDING_CLOUDS, ONBOARDING_DATA_PLANE_NAME_MAX_LENGTH, ONBOARDING_DATA_PLANE_NAME_PATTERN, ONBOARDING_DOMAIN_MAX_LENGTH, ONBOARDING_DOMAIN_PATTERN, ONBOARDING_ENTERPRISE_DOMAIN_MESSAGE, ONBOARDING_OWNER_DOMAIN_MESSAGE, ONBOARDING_OWNER_EMAIL_MAX_LENGTH, ONBOARDING_SIGN_IN_NOT_OFFERED_MESSAGE, ONBOARDING_TENANCIES, ONBOARDING_TENANT_NAME_MAX_LENGTH, ONBOARDING_TENANT_NAME_PATTERN, ONBOARDING_TENANT_SLUG_MAX_LENGTH, RESERVED_ORG_SLUGS, RESERVED_ORG_SLUG_MESSAGE, onboardingAllowedDomainsSchema, onboardingAppSlugSchema, onboardingAppSlugsSchema, onboardingDomainSchema, onboardingOwnerDomainAllowed, onboardingOwnerEmailSchema, onboardingRequestIdParamSchema, onboardingRequestRecordSchema, onboardingRequestSchema, onboardingRequestViewSchema, onboardingTenantSlugSchema, signInSecretOf, withoutSignInSecret };

//# sourceMappingURL=onboarding-request.js.map